import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCamera,
  FiLock,
  FiMail,
  FiMapPin,
  FiPhone,
  FiUser,
  FiX,
} from "react-icons/fi";
import {
  useGetGendersQuery,
  useGetMaritalStatusesQuery,
  useGetOccupationsQuery,
  useGetRelationshipStatusesQuery,
  useGetTitlesQuery,
} from "../../store/selectableSlice";
import { isValidPhoneFormat, normalizePhoneNumber } from "../../utils/phoneUtils";
import {
  ActionBar,
  Alert,
  Button,
  DateField,
  FocusFirstError,
  PhoneField,
  SelectField,
  StepProgress,
  TextField,
  focusFirstInvalid,
} from "../Auth";

const STEPS = ["About you", "Contact", "Next of kin", "Review and pay"];
const DRAFT_KEY = "signup_wizard_draft_v1";
const DRAFT_MAX_AGE_MS = 2 * 60 * 60 * 1000;
const DEFAULT_COUNTRY_CODE = "234";

// Fields that belong to each step. Used to check one step at a time.
const STEP_FIELDS = [
  ["branch_id", "title_id", "first_name", "other_name", "last_name", "occupation_id", "occupation"],
  ["phone", "email", "address", "gender_id", "dob", "marital_id"],
  ["next_of_kin_name", "next_of_kin_phone", "relationship_status_id"],
  [],
];

const EMPTY_VALUES = {
  branch_id: "",
  title_id: "",
  first_name: "",
  other_name: "",
  last_name: "",
  occupation_id: "",
  occupation: "",
  phone: "",
  email: "",
  address: "",
  gender_id: "",
  dob: "",
  marital_id: "",
  next_of_kin_name: "",
  next_of_kin_phone: "",
  relationship_status_id: "",
  member_no: "",
};

const MAX_PHOTO_MB = 10;
const PHOTO_TYPES = ["jpeg", "jpg", "png"];

export const formatNaira = (amount = 0) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "₦0.00";
  return `₦${n.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const isVoluntary = (fee) => !fee.amount && fee.amount !== 0;

function readDraft() {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft || Date.now() - (draft.savedAt || 0) > DRAFT_MAX_AGE_MS) return null;
    return draft;
  } catch {
    return null;
  }
}

export function clearSignupDraft() {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* storage can be blocked; nothing to clear */
  }
}

/** Keeps what the member has typed (never the photo) so a refresh does not wipe four screens of work. */
function DraftSaver({ step }) {
  const { values } = useFormikContext();
  useEffect(() => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ values, step, savedAt: Date.now() }));
    } catch {
      /* ignore */
    }
  }, [values, step]);
  return null;
}

function buildSchema(hasBranches) {
  const phone = (message) =>
    Yup.string()
      .required(message)
      .test("phone", "Enter a valid phone number, like 0801 234 5678", (v) =>
        isValidPhoneFormat(v, DEFAULT_COUNTRY_CODE)
      );
  return Yup.object({
    branch_id: hasBranches
      ? Yup.string().required("Choose the branch you belong to")
      : Yup.string().notRequired(),
    title_id: Yup.string().required("Choose your title"),
    first_name: Yup.string().trim().required("Enter your first name"),
    other_name: Yup.string().notRequired(),
    last_name: Yup.string().trim().required("Enter your last name"),
    occupation_id: Yup.string().required("Choose your occupation"),
    occupation: Yup.string().when("occupation_id", {
      is: "others",
      then: (schema) => schema.trim().required("Tell us your occupation"),
      otherwise: (schema) => schema.notRequired(),
    }),
    phone: phone("Enter your phone number"),
    email: Yup.string().trim().email("Enter a full email address, like name@gmail.com").notRequired(),
    address: Yup.string().trim().required("Enter your home address"),
    gender_id: Yup.string().required("Choose your gender"),
    dob: Yup.string()
      .required("Enter your date of birth")
      .test("not-future", "Date of birth cannot be in the future", (v) => !v || v <= todayIso()),
    marital_id: Yup.string().required("Choose your marital status"),
    next_of_kin_name: Yup.string().trim().required("Enter your next of kin's full name"),
    next_of_kin_phone: phone("Enter your next of kin's phone number"),
    relationship_status_id: Yup.string().required("Choose their relationship to you"),
  });
}

function todayIso() {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

const toOptions = (list = []) => list.map((item) => ({ value: String(item.id), label: item.name }));

/* ------------------------------------------------------------------ */
/* Step 4 helpers                                                      */
/* ------------------------------------------------------------------ */

function useFeeSelection(fees) {
  const [checked, setChecked] = useState({});
  const [amounts, setAmounts] = useState({});

  const feeKey = fees.map((f) => f.id).join(",");
  useEffect(() => {
    setChecked((prev) =>
      fees.reduce((acc, fee) => {
        acc[fee.id] = prev[fee.id] ?? fee.category === "Compulsory";
        return acc;
      }, {})
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feeKey]);

  const selected = fees
    .filter((fee) => checked[fee.id])
    .map((fee) => ({
      id: fee.id,
      amount: isVoluntary(fee) ? parseFloat(amounts[fee.id] || 0) || 0 : parseFloat(fee.amount || 0),
    }));
  const total = selected.reduce((sum, item) => sum + item.amount, 0);
  const missingAmount = fees.filter(
    (fee) => checked[fee.id] && isVoluntary(fee) && !(parseFloat(amounts[fee.id]) > 0)
  );

  return {
    checked,
    amounts,
    selected,
    total,
    missingAmount,
    toggle: (id) => setChecked((prev) => ({ ...prev, [id]: !prev[id] })),
    setAmount: (id, value) => setAmounts((prev) => ({ ...prev, [id]: value })),
  };
}

function FeeList({ fees, selection, showMissing }) {
  return (
    <div>
      {fees.map((fee) => {
        const compulsory = fee.category === "Compulsory";
        const open = isVoluntary(fee);
        const isOn = Boolean(selection.checked[fee.id]);
        const missing = showMissing && isOn && open && !(parseFloat(selection.amounts[fee.id]) > 0);
        const inputId = `fee-${fee.id}`;
        return (
          <div className="ax-fee" key={fee.id}>
            <div className="ax-fee-row">
              <label htmlFor={inputId}>
                <input
                  id={inputId}
                  type="checkbox"
                  checked={isOn}
                  disabled={compulsory}
                  onChange={() => selection.toggle(fee.id)}
                />
                <span>
                  {fee.name}
                  {compulsory ? <span className="ax-optional"> (required)</span> : null}
                </span>
              </label>
              <span className="ax-money">{open ? "You choose" : formatNaira(fee.amount)}</span>
            </div>
            {isOn && open ? (
              <div className="ax-field" style={{ marginLeft: 34 }}>
                <label className="ax-label" htmlFor={`${inputId}-amount`}>
                  Amount you want to pay
                </label>
                <input
                  id={`${inputId}-amount`}
                  className="ax-input"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  placeholder="₦0.00"
                  value={selection.amounts[fee.id] || ""}
                  aria-invalid={missing ? "true" : undefined}
                  aria-describedby={missing ? `${inputId}-err` : undefined}
                  onChange={(e) => selection.setAmount(fee.id, e.target.value)}
                />
                {missing ? (
                  <p className="ax-error" id={`${inputId}-err`} role="alert">
                    Enter an amount greater than zero.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function SummarySection({ title, onEdit, rows }) {
  return (
    <section>
      <h3 className="ax-section-title">
        {title}
        <button type="button" className="ax-link" onClick={onEdit}>
          Edit
        </button>
      </h3>
      <dl className="ax-dl">
        {rows
          .filter(([, value]) => value)
          .map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
      </dl>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Wizard                                                              */
/* ------------------------------------------------------------------ */

export default function PublicSignupWizard({
  branches = [],
  isLoadingBranches = false,
  fees = [],
  isLoadingFees = false,
  onSubmit,
  submitError = "",
}) {
  const draft = useMemo(() => readDraft(), []);
  const [step, setStep] = useState(() => Math.min(draft?.step ?? 0, STEPS.length - 1));
  const [photo, setPhoto] = useState(null);
  const [photoError, setPhotoError] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showFeeErrors, setShowFeeErrors] = useState(false);
  const headingRef = useRef(null);
  const firstRender = useRef(true);

  const { data: titles = [] } = useGetTitlesQuery();
  const { data: genders = [] } = useGetGendersQuery();
  const { data: maritalStatuses = [] } = useGetMaritalStatusesQuery();
  const { data: relationshipStatuses = [] } = useGetRelationshipStatusesQuery();
  const { data: occupations = [] } = useGetOccupationsQuery();

  const branchList = Array.isArray(branches) ? branches : [];
  const hasBranches = branchList.length > 0;
  const schema = useMemo(() => buildSchema(hasBranches), [hasBranches]);
  const selection = useFeeSelection(fees);

  // Preview URL for the chosen photo, released when it changes or the page closes.
  useEffect(() => {
    if (!photo) {
      setPhotoUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(photo);
    setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  // Each new step starts at the top, with focus on the heading for screen readers.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    headingRef.current?.focus({ preventScroll: true });
  }, [step]);

  const initialValues = useMemo(() => {
    const saved = draft?.values || {};
    return { ...EMPTY_VALUES, ...saved };
  }, [draft]);

  function pickPhoto(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    const type = file.type?.split("/")[1];
    if (file.size / 1024 / 1024 > MAX_PHOTO_MB || !PHOTO_TYPES.includes(type)) {
      setPhoto(null);
      setPhotoError(`Use a JPG or PNG under ${MAX_PHOTO_MB}MB.`);
      event.target.value = "";
      return;
    }
    setPhotoError("");
    setPhoto(file);
  }

  async function goNext(formik) {
    const fields = STEP_FIELDS[step];
    formik.setTouched(
      fields.reduce((acc, f) => ({ ...acc, [f]: true }), { ...formik.touched }),
      false
    );
    const errors = await formik.validateForm();
    if (fields.some((f) => errors[f])) {
      // Make sure the errors render before we look for the first one.
      formik.setErrors(errors);
      focusFirstInvalid();
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  async function handlePay(values) {
    // Payment only ever starts from the review step, however the form was submitted.
    if (step !== STEPS.length - 1) return;
    setShowFeeErrors(true);
    if (selection.missingAmount.length > 0) {
      focusFirstInvalid();
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        ...values,
        phone: normalizePhoneNumber(values.phone, DEFAULT_COUNTRY_CODE) || values.phone,
        next_of_kin_phone:
          normalizePhoneNumber(values.next_of_kin_phone, DEFAULT_COUNTRY_CODE) ||
          values.next_of_kin_phone,
        email: values.email?.trim() || "",
        payment_method: "PAYSTACK",
        imgFile: photo,
        selectedBreakdown: selection.selected.map((item) => item.id),
        selectedBreakdownWithAmounts: selection.selected,
        selectedAmount: selection.total,
      });
    } finally {
      setSubmitting(false);
    }
  }

  const lookup = (list, id) => list.find((item) => String(item.id) === String(id))?.name || "";

  return (
    <>
      <StepProgress steps={STEPS} current={step} />

      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        validateOnMount={false}
        onSubmit={handlePay}
      >
        {(formik) => {
          const { values, setFieldValue } = formik;
          const fullName = [lookup(titles, values.title_id), values.first_name, values.other_name, values.last_name]
            .filter(Boolean)
            .join(" ");
          return (
            <Form noValidate className="ax-card" aria-label={`Member sign-up, ${STEPS[step]}`}>
              <DraftSaver step={step} />
              <FocusFirstError />

              {/* ---------------- Step 1 ---------------- */}
              {step === 0 ? (
                <>
                  <div>
                    <h1 className="ax-h1" tabIndex={-1} ref={headingRef}>
                      Tell us about you
                    </h1>
                    <p className="ax-sub">Use your name as it appears on your ID.</p>
                  </div>
                  {hasBranches || isLoadingBranches ? (
                    <SelectField
                      name="branch_id"
                      label="Your branch"
                      placeholder={isLoadingBranches ? "Loading branches" : "Choose your branch"}
                      options={branchList.map((b) => ({ value: String(b.id), label: b.name }))}
                      disabled={isLoadingBranches}
                    />
                  ) : null}
                  <SelectField name="title_id" label="Title" placeholder="Choose your title" options={toOptions(titles)} />
                  <div className="ax-grid2">
                    <TextField
                      name="first_name"
                      label="First name"
                      icon={FiUser}
                      autoComplete="given-name"
                    />
                    <TextField
                      name="last_name"
                      label="Last name"
                      autoComplete="family-name"
                    />
                  </div>
                  <TextField
                    name="other_name"
                    label="Middle name"
                    optional
                    autoComplete="additional-name"
                  />
                  <SelectField
                    name="occupation_id"
                    label="Occupation"
                    placeholder="Choose your occupation"
                    options={[...toOptions(occupations), { value: "others", label: "Others" }]}
                    onValueChange={(value) => {
                      if (value === "others") {
                        setFieldValue("occupation", "");
                      } else {
                        setFieldValue("occupation", lookup(occupations, value));
                      }
                    }}
                  />
                  {values.occupation_id === "others" ? (
                    <TextField
                      name="occupation"
                      label="Your occupation"
                      placeholder="For example, tailor"
                    />
                  ) : null}
                </>
              ) : null}

              {/* ---------------- Step 2 ---------------- */}
              {step === 1 ? (
                <>
                  <div>
                    <h1 className="ax-h1" tabIndex={-1} ref={headingRef}>
                      How can we reach you?
                    </h1>
                    <p className="ax-sub">We use these details to contact you about your membership.</p>
                  </div>
                  <PhoneField
                    name="phone"
                    label="Phone number"
                    icon={FiPhone}
                    hint="Nigerian numbers like 0801 234 5678 work. For other countries start with + and the country code."
                  />
                  <TextField
                    name="email"
                    label="Email"
                    optional
                    icon={FiMail}
                    type="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    placeholder="name@gmail.com"
                  />
                  <TextField
                    name="address"
                    label="Home address"
                    icon={FiMapPin}
                    autoComplete="street-address"
                    placeholder="Street, area, city"
                  />
                  <div className="ax-grid2">
                    <SelectField
                      name="gender_id"
                      label="Gender"
                      placeholder="Choose"
                      options={toOptions(genders)}
                    />
                    <DateField name="dob" label="Date of birth" max={todayIso()} />
                  </div>
                  <SelectField
                    name="marital_id"
                    label="Marital status"
                    placeholder="Choose"
                    options={toOptions(maritalStatuses)}
                  />
                </>
              ) : null}

              {/* ---------------- Step 3 ---------------- */}
              {step === 2 ? (
                <>
                  <div>
                    <h1 className="ax-h1" tabIndex={-1} ref={headingRef}>
                      Who should we contact in an emergency?
                    </h1>
                    <p className="ax-sub">Your next of kin. They are not signed up for anything.</p>
                  </div>
                  <TextField
                    name="next_of_kin_name"
                    label="Full name"
                    icon={FiUser}
                    autoComplete="off"
                  />
                  <PhoneField name="next_of_kin_phone" label="Phone number" icon={FiPhone} />
                  <SelectField
                    name="relationship_status_id"
                    label="Relationship to you"
                    placeholder="Choose"
                    options={toOptions(relationshipStatuses)}
                  />

                  <div className="ax-field">
                    <div className="ax-label">
                      <span>
                        Profile photo <span className="ax-optional">(optional)</span>
                      </span>
                    </div>
                    <div className="ax-photo">
                      <div className="ax-photo-preview">
                        {photoUrl ? (
                          <img src={photoUrl} alt="Your chosen profile" />
                        ) : (
                          <FiCamera aria-hidden="true" size={24} />
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <label className="ax-btn ax-btn--outline" style={{ width: "auto", cursor: "pointer" }}>
                          {photo ? "Change photo" : "Choose photo"}
                          <input
                            className="ax-sr"
                            type="file"
                            accept="image/png, image/jpeg"
                            onChange={pickPhoto}
                          />
                        </label>
                        {photo ? (
                          <button type="button" className="ax-link" onClick={() => setPhoto(null)}>
                            <FiX aria-hidden="true" size={16} />
                            Remove
                          </button>
                        ) : null}
                      </div>
                    </div>
                    {photoError ? (
                      <p className="ax-error" role="alert">
                        {photoError}
                      </p>
                    ) : (
                      <p className="ax-hint">JPG or PNG, up to {MAX_PHOTO_MB}MB. You can add one later.</p>
                    )}
                  </div>
                </>
              ) : null}

              {/* ---------------- Step 4 ---------------- */}
              {step === 3 ? (
                <>
                  <div>
                    <h1 className="ax-h1" tabIndex={-1} ref={headingRef}>
                      Review and pay
                    </h1>
                    <p className="ax-sub">Check your details, then pay your registration fees online.</p>
                  </div>

                  {submitError ? <Alert tone="error">{submitError}</Alert> : null}

                  <SummarySection
                    title="You"
                    onEdit={() => setStep(0)}
                    rows={[
                      ["Name", fullName],
                      ["Branch", lookup(branchList, values.branch_id)],
                      [
                        "Occupation",
                        values.occupation_id === "others" ? values.occupation : lookup(occupations, values.occupation_id),
                      ],
                    ]}
                  />
                  <SummarySection
                    title="Contact"
                    onEdit={() => setStep(1)}
                    rows={[
                      ["Phone", values.phone],
                      ["Email", values.email],
                      ["Address", values.address],
                      ["Date of birth", values.dob],
                    ]}
                  />
                  <SummarySection
                    title="Next of kin"
                    onEdit={() => setStep(2)}
                    rows={[
                      ["Name", values.next_of_kin_name],
                      ["Phone", values.next_of_kin_phone],
                      ["Relationship", lookup(relationshipStatuses, values.relationship_status_id)],
                    ]}
                  />

                  <section>
                    <h3 className="ax-section-title">Registration fees</h3>
                    {isLoadingFees ? (
                      <span className="ax-skel" style={{ height: 72, marginTop: 8 }} aria-busy="true" />
                    ) : fees.length === 0 ? (
                      <Alert tone="error">
                        We could not load the registration fees. Refresh the page and try again.
                      </Alert>
                    ) : (
                      <>
                        <FeeList fees={fees} selection={selection} showMissing={showFeeErrors} />
                        <div className="ax-total">
                          <span>Total to pay</span>
                          <span className="ax-money">{formatNaira(selection.total)}</span>
                        </div>
                      </>
                    )}
                  </section>

                  <Alert tone="info">
                    You pay on Paystack&apos;s secure page, so we never see your card details. Paystack
                    shows the final amount before you confirm.
                  </Alert>
                </>
              ) : null}

              {/* ---------------- Actions ---------------- */}
              <ActionBar sticky>
                {step === 3 ? (
                  <Button
                    key="pay"
                    type="submit"
                    loading={submitting}
                    disabled={isLoadingFees || fees.length === 0}
                  >
                    <FiLock aria-hidden="true" size={16} />
                    Pay {formatNaira(selection.total)}
                  </Button>
                ) : (
                  // A different key makes React swap the element, so the click that opens the
                  // review step can never also submit the payment.
                  <Button key="next" onClick={() => goNext(formik)}>
                    {step === 2 ? "Review" : "Continue"}
                    <FiArrowRight aria-hidden="true" size={18} />
                  </Button>
                )}
                {step > 0 ? (
                  <Button
                    variant="outline"
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={submitting}
                  >
                    <FiArrowLeft aria-hidden="true" size={18} />
                    Back
                  </Button>
                ) : null}
              </ActionBar>
            </Form>
          );
        }}
      </Formik>
    </>
  );
}
